import { Link } from 'react-router-dom';
import ArticleLayout from '../../components/ArticleLayout';

const linkClass = 'text-[#B5531E] hover:underline underline-offset-4';

export default function RadiatorVarmerSelvomDenErSlukket() {
  return (
    <ArticleLayout
      title="Radiatoren bliver ved med at varme, selvom den er slukket"
      metaTitle="Radiatoren varmer, selvom den er slukket: Årsager"
      metaDescription="Kan radiatoren ikke slukkes? Se årsagerne i rækkefølge, en test du selv kan lave med ventilstiften, og hvornår det er arbejde for en VVS-installatør."
      canonicalPath="/radiator-varmer-selvom-den-er-slukket"
      datePublished="2026-11-03"
      dateModified="2026-11-03"
      introLead="Når en radiator bliver ved med at varme, selvom termostaten er skruet helt ned, kan det skyldes termostathovedet, ventilstiften eller pakdåsen. Nogle gange er det rørene og ikke radiatoren, der er varme."
      introAnswer="Tjek i denne rækkefølge: Er det radiatoren eller rørene, der varmer? Sidder termostathovedet fast? Går ventilstiften frit? Er hovedet beskadiget? Lukker ventilen stadig ikke, skal en autoriseret VVS-installatør se på den."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Fra det enkle til fagmanden' },
        { id: 'roer', title: '2. Radiatoren eller rørene?' },
        { id: 'termostathoved', title: '3. Termostathovedet er løst eller beskadiget' },
        { id: 'test', title: '4. Test: Tag hovedet af, og tryk stiften ind' },
        { id: 'graense', title: '5. Det må du selv, og her stopper du' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Når radiatoren ikke kan slukkes"
      checklist={[
        {
          title: 'Mærk efter, om det er radiatoren eller rørene, der er varme',
          description: 'Rørene kan være varme, fordi vandet stadig cirkulerer i anlægget.',
        },
        {
          title: 'Tjek, at termostathovedet sidder fast',
          description: 'Et løst hoved har ikke forbindelse til ventilstiften, og så står ventilen helt åben.',
        },
        {
          title: 'Spænd kraven bag termostathovedet',
          description: 'Stram skruen i kraven med en unbrakonøgle, eller drej plastkraven med uret på nyere modeller.',
        },
        {
          title: 'Tag hovedet af, og tryk ventilstiften ind',
          description: 'Stiften skal tydeligt kunne bevæge sig et par millimeter og fjedre tilbage.',
        },
        {
          title: 'Skift termostathovedet, hvis stiften går frit',
          description: 'Når ventilen virker, kan hovedet skiftes uden at tømme radiatoren for vand.',
        },
        {
          title: 'Ring til en autoriseret VVS-installatør, hvis ventilen ikke lukker',
          description: 'Pakdåse, ventilhus og rør er en del af vandsystemet og ikke gør-det-selv-arbejde.',
        },
      ]}
      sections={[
        {
          id: 'roer',
          title: 'Er det radiatoren eller rørene, der varmer?',
          content: (
            <>
              <p>
                Rørene til en slukket radiator kan være varme, fordi vandet cirkulerer i centralvarmeanlægget, medmindre der også er lukket for cirkulationen og skiftet til sommerdrift. Mange anlæg gør det selv, fx når udetemperaturen kommer over 17 grader. I etageejendomme slukkes centralvarmen ofte på en bestemt dato.
              </p>
              <p>
                Er selve radiatoren kold, mens rørene er varme, fejler radiatoren ikke.
              </p>
            </>
          ),
        },
        {
          id: 'termostathoved',
          title: 'Termostathovedet er løst eller beskadiget',
          content: (
            <>
              <p>
                <strong>Løst hoved:</strong> Har termostathovedet løsnet sig, har det ikke forbindelse til ventilstiften. Vandet løber så frit gennem en helt åben ventil, og radiatoren varmer maksimalt. Skub hovedet på plads, og stram skruen i den sorte krave med en unbrakonøgle. På nyere modeller uden skrue drejer du plastkraven med uret.
              </p>
              <p>
                <strong>Beskadiget hoved:</strong> Et slag kan også ødelægge forbindelsen til stiften. Kan hovedet ikke fastgøres igen, skal det skiftes. Løsn skruen, eller drej kraven mod uret, og sæt et nyt på. Termostathoveder fås i byggemarkeder.
              </p>
              <p>
                Se også{' '}
                <Link to="/radiator-termostat" className={linkClass}>
                  hvordan en radiatortermostat er bygget op
                </Link>
                .
              </p>
            </>
          ),
        },
        {
          id: 'test',
          title: 'Test: Tag hovedet af, og tryk stiften ind',
          content: (
            <>
              <p>
                Under termostathovedet sidder ventilstiften. Trykkes den ind, lukkes der for vandet. Slippes den, åbnes der igen.
              </p>
              <p>
                <strong>Stiften går frit:</strong> Bevæger den sig tydeligt et par millimeter og fjedrer tilbage, virker ventilen. Fejlen sidder så sandsynligvis i termostathovedet.
              </p>
              <p>
                <strong>Stiften sidder fast:</strong> Motionér den roligt frem og tilbage med håndtaget på en skruetrækker eller en svensknøgle. Træk aldrig i stiften, da det kan ødelægge pakdåsen, så ventilen lækker. Vil den stadig ikke bevæge sig, skal ventilen skiftes, og det kræver, at systemet tømmes.
              </p>
              <p>
                <strong>Radiatoren bliver stadig varm:</strong> Varmer radiatoren, mens stiften er trykket ind, lukker ventilen ikke. Fejlen sidder da i ventilen eller pakdåsen, ikke i termostathovedet.
              </p>
              <p>
                Det modsatte problem, hvor radiatoren ikke vil varme, er beskrevet under{' '}
                <Link to="/radiator-ventil-sidder-fast" className={linkClass}>
                  ventilstiften sidder fast efter sommeren
                </Link>
                .
              </p>
            </>
          ),
        },
        {
          id: 'graense',
          title: 'Det må du selv, og her stopper du',
          content: (
            <>
              <p>
                Du må selv gøre det, der ikke åbner for anlæggets vand: tage termostathovedet af, motionere stiften, dreje og spænde hovedet og skifte batteri i en elektronisk termostat. En elektronisk termostat regulerer ventilen som et almindeligt hoved, så testen med stiften virker også her. Batteri og forbindelse tjekkes efter producentens vejledning.
              </p>
              <p>
                <strong>Alt, der åbner for vandet, skal udføres af en autoriseret VVS-installatør.</strong> Det gælder pakdåsen, ventilhuset og rørene.
              </p>
              <p>
                Nogle radiatorer har en{' '}
                <Link to="/radiator-returventil" className={linkClass}>
                  returventil i bunden
                </Link>
                , der kan lukke radiatoren af fra rørsystemet. Den bruges også til indregulering, så læs om den, før du drejer på den. Drypper der vand fra ventilen, så begræns skaden først, og læs{' '}
                <Link to="/radiator-laekker" className={linkClass}>
                  hvad du gør, når radiatoren lækker
                </Link>
                .
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal du ringe til en autoriseret VVS-installatør?',
        content: (
          <p>
            Når stiften ikke vil bevæge sig, når radiatoren varmer, mens stiften er trykket ind, eller når der drypper vand fra ventilen. Bor du til leje, så kontakt din udlejer, før du går i gang.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Er det normalt, at rørene er varme, når radiatoren er slukket?',
          a: 'Ja. Vandet cirkulerer i centralvarmeanlægget, medmindre der også er lukket for cirkulationen og skiftet til sommerdrift.',
        },
        {
          q: 'Løber der vand ud, når jeg tager termostathovedet af?',
          a: 'Nej, ikke når ventilen er i orden. Hovedet sidder uden på ventilen og kan skiftes uden at tømme radiatoren for vand.',
        },
        {
          q: 'Må jeg trække ventilstiften ud med en tang?',
          a: 'Nej. Det kan ødelægge pakdåsen, så ventilen lækker. Tryk stiften ind, og lad den fjedre ud af sig selv.',
        },
        {
          q: 'Kan jeg selv skifte pakdåsen?',
          a: 'Pakdåsen sidder i ventilen, hvor anlæggets vand er. Arbejde på pakdåse, ventilhus og rør skal udføres af en autoriseret VVS-installatør.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'det modsatte problem: radiatoren vil ikke varme' },
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'opbygning, typer og tegn på slid' },
        { to: '/radiator-returventil', text: 'Returventil og forindstilling', desc: 'ventilen i bunden af radiatoren' },
        { to: '/radiator-laekker', text: 'Radiatoren lækker vand', desc: 'begræns skaden først' },
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den samlede tjekliste' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/radiatoren-varmer-loes-selv-om-jeg-har-lukket-for-den-hvad-goer-jeg-92528', label: 'Radiator er varm, selvom den er slukket – sådan gør du' },
        { url: 'https://www.bolius.dk/roer-til-radiator-er-varme-selv-naar-radiatoren-er-slukket-100026', label: 'Rør til radiator er varme, selv når radiatoren er slukket?' },
        { url: 'https://www.bolius.dk/ingen-varme-i-din-radiator-saadan-faar-du-varme-i-den-igen-26455', label: 'Ingen varme i din radiator? Her er 3 løsninger til at få den til at virke igen' },
        { url: 'https://www.purmo.com/da-dk/blogartikler/udskiftning-af-radiatorventil-dette-skal-du-vaere-opmaerksom-pa/', label: 'Udskiftning af radiatorventil – dette skal du være opmærksom på - Purmo DK' },
        { url: 'https://byggeriogenergi.dk/enfamiliehuse/udskiftning-af-radiatorventil-termostatstyring', label: 'Udskiftning af radiatorventil / termostatstyring | Byggeri og energi' },
      ]}
    />
  );
}
